import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import i18n from 'src/i18n';
import { fetchCapabilities } from 'src/models/capability/capability';
import { setupSentryUserContext } from 'src/utils/sentry';

import { fetchSession } from '../models/session/session';
import { fetchCurrentTrainer } from '../models/trainer/trainer';
import { fetchCurrentUser, fetchUser, updateUser } from '../models/user/user';

import { useNativeBridge } from './NativeBridgeContext';

// Import types
import type { Session } from '../models/session/_types';
import type { Trainer } from '../models/trainer/_types';
import type { SessionUser, User } from '../models/user/_types';
import type { Capability } from 'src/models/capability/_types';

interface SessionContextProps {
  session: Session | undefined;
  sessionLoading: boolean;
  sessionError: any;
  user: SessionUser | null | undefined;
  userLoading: boolean;
  userError: any;
  trainer: Trainer | undefined;
  trainerLoading: boolean;
  trainerError: any;
  capabilities: Capability[] | undefined;
  capabilitiesLoading: boolean;
  capabilitiesError: any;
  sessionTrainer: User | undefined;
  sessionTrainerLoading: boolean;
  sessionTrainerError: any;
  setDarkmode: (darkmode: boolean) => void;
  isDarkmode: () => boolean;
  updateCurrentUser: (user: Partial<User>) => void;
  disableDarkmode: () => void;
  enableDarkmode: () => void;
  refreshSessionAndUser: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasLoadedClients: boolean;
  setHasLoadedClients: (hasLoadedClients: boolean) => void;
  darkmodeDisabled: boolean;
}

interface RequiredSessionContextProps
  extends Omit<
    SessionContextProps,
    'session' | 'user' | 'trainer' | 'sessionTrainer' | 'capabilities'
  > {
  session: Session;
  user: SessionUser;
  trainer: Trainer;
  sessionTrainer: User;
  capabilities: Capability[];
}

interface SessionContextProviderProps {
  children: ReactNode;
}

const SessionContext = createContext<SessionContextProps>({
  isDarkmode: () => false,
} as SessionContextProps);

const SessionContextProvider: React.FC<SessionContextProviderProps> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const [darkmodeDisabled, setDarkmodeDisabled] = useState(false);
  const [hasLoadedClients, setHasLoadedClients] = useState(false);

  // NativeBridge needs to force darkmode via query params
  const nativeBridge = useNativeBridge();

  const {
    data: session,
    isLoading: sessionLoading,
    error: sessionError,
  } = useQuery({
    queryKey: ['session'],
    queryFn: () => {
      return fetchSession('');
    },
  });

  const { authToken } = nativeBridge;
  const {
    data: user,
    isLoading: userLoading,
    error: userError,
  } = useQuery({
    queryKey: ['currentUser', authToken],
    queryFn: () => {
      return fetchCurrentUser({ authToken, withImpersonator: true });
    },
    gcTime: 0,
  });

  const {
    data: trainer,
    isLoading: trainerLoading,
    error: trainerError,
  } = useQuery({
    queryKey: ['currentTrainer', user?.id],
    queryFn: () => {
      return fetchCurrentTrainer();
    },
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    gcTime: 0,
  });

  const {
    data: sessionTrainer,
    isLoading: sessionTrainerLoading,
    error: sessionTrainerError,
  } = useQuery({
    queryKey: ['user', session?.trainerId],
    queryFn: () => {
      if (!session?.trainerId) {
        throw new Error('Trainer ID is not available');
      }

      return fetchUser({ id: session.trainerId });
    },
    enabled: !!session?.trainerId,
  });

  const {
    data: capabilities,
    isLoading: capabilitiesLoading,
    error: capabilitiesError,
  } = useQuery({
    queryKey: ['capabilities', user?.id],
    queryFn: () => {
      return fetchCapabilities();
    },
    enabled: !!user?.id,
    gcTime: 0,
  });

  const userMutation = useMutation({
    mutationFn: (partialUser: Partial<User>) => {
      if (!user?.id) {
        throw new Error('Attempted user update without ID');
      }
      return updateUser(user.id, partialUser);
    },
    onMutate: (partialUser: Partial<User>) => {
      queryClient.setQueryData(['currentUser', authToken], {
        ...user,
        ...partialUser,
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['currentUser', authToken], data);
    },
  });

  const isDarkmode = useCallback(() => {
    // Checks for darkmode query param
    if (nativeBridge.darkMode !== null) {
      return nativeBridge.darkMode;
    }

    if (darkmodeDisabled) {
      return false;
    }

    // need to check here, because session not avaialble in the native bridge
    if (nativeBridge.isWebview) {
      if (session?.theme?.forcedTheme === 'dark') {
        return true;
      } else if (session?.theme?.forcedTheme === 'light') {
        return false;
      }
    }

    if (user?.useDarkMode !== undefined) {
      return user.useDarkMode;
    }

    return (
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    );
  }, [
    user?.useDarkMode,
    nativeBridge.darkMode,
    darkmodeDisabled,
    session?.theme?.forcedTheme,
    nativeBridge.isWebview,
  ]);

  const updateCurrentUser = useCallback(
    (partialUser: Partial<User>) => {
      userMutation.mutate(partialUser);
    },
    [userMutation]
  );

  const setDarkmode = useCallback(
    (darkmode: boolean) => {
      updateCurrentUser({ useDarkMode: darkmode });
    },
    [updateCurrentUser]
  );

  const disableDarkmode = useCallback(() => {
    setDarkmodeDisabled(true);
  }, []);

  const enableDarkmode = useCallback(() => {
    setDarkmodeDisabled(false);
  }, []);

  useEffect(() => {
    async function loadTranslation() {
      // Set the language based on priority: user locale > session locale > default 'en'
      const locale = user?.locale || session?.locale || 'en';
      i18n.changeLanguage(locale);

      // Load platform-specific translations if session ID exists
      if (session?.id && locale === 'en') {
        try {
          const translations = await import(
            `src/locales/en-US-platforms/translation-${session.id}.json`
          );
          i18n.addResourceBundle(
            `en-US-${session.id}`,
            'translation',
            translations.default,
            true,
            true
          );
          i18n.changeLanguage(`en-US-${session.id}`);
          // eslint-disable-next-line no-empty
        } catch (error) {}
      }
    }

    loadTranslation();
  }, [session?.id, user?.locale, session?.subdomain, session?.locale]);

  // Set Sentry user context and start replay for trainers when user loads or changes
  useEffect(() => {
    setupSentryUserContext(user);
  }, [user]);

  const refreshUser = useCallback(async () => {
    try {
      await queryClient.refetchQueries({
        queryKey: ['currentUser'],
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to refresh user:', error);
    }
  }, [queryClient]);

  const refreshSessionAndUser = useCallback(async () => {
    try {
      await queryClient.refetchQueries({
        queryKey: ['session'],
      });
      await queryClient.refetchQueries({
        queryKey: ['currentUser'],
      });
      await queryClient.refetchQueries({
        queryKey: ['user', session?.trainerId],
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to refresh session and user:', error);
    }
  }, [queryClient, session?.trainerId]);

  const contextValue = useMemo<SessionContextProps>(() => {
    return {
      session,
      sessionError,
      sessionLoading,
      user,
      userLoading,
      userError,
      trainer,
      trainerLoading,
      trainerError,
      sessionTrainer,
      sessionTrainerLoading,
      sessionTrainerError,
      capabilities,
      capabilitiesLoading,
      capabilitiesError,
      updateCurrentUser,
      setDarkmode,
      isDarkmode,
      darkmodeDisabled,
      disableDarkmode,
      enableDarkmode,
      refreshUser,
      refreshSessionAndUser,
      hasLoadedClients,
      setHasLoadedClients,
    };
  }, [
    session,
    sessionError,
    sessionLoading,
    user,
    userLoading,
    userError,
    trainer,
    trainerLoading,
    trainerError,
    sessionTrainer,
    sessionTrainerLoading,
    sessionTrainerError,
    capabilities,
    capabilitiesLoading,
    capabilitiesError,
    updateCurrentUser,
    setDarkmode,
    isDarkmode,
    darkmodeDisabled,
    disableDarkmode,
    enableDarkmode,
    refreshUser,
    refreshSessionAndUser,
    hasLoadedClients,
    setHasLoadedClients,
  ]);

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  );
};

function useSession(): RequiredSessionContextProps {
  const context = useContext(SessionContext);
  const { user, session } = context;
  if (!user?.id || !session?.id) {
    throw new Error(
      'User/Session accessed through useSession without being present'
    );
  }
  return context as RequiredSessionContextProps;
}

function useOptionalSession(): SessionContextProps {
  return useContext(SessionContext);
}

export {
  useOptionalSession,
  useSession,
  SessionContextProvider,
  SessionContext,
  type User,
  type Session,
};
